export interface SiemensCapabilityCard {
  id: string;
  tag: string; // PLC, HMI, DRV, MOT, UPG, EPC
  title: string;
  description: string;
  image?: string;
  iconName?: string;
  actionText?: string;
  actionTarget?: string;
}

export interface SiemensWorkflowStep {
  num: string; // 01, 02, 03, 04
  title: string;
  description: string;
  phaseLabel?: string;
  iconName?: string;
}

export interface SiemensIndustryItem {
  id: string;
  name: string;
  detail: string;
  tag?: string;
  iconName?: string;
  image?: string;
  link?: string;
}

export interface SiemensCertStep {
  title: string;
  description: string;
}

export interface SiemensProofMetric {
  title: string;
  description: string;
  value?: string;
  label?: string;
  isCyan?: boolean;
  iconName?: string;
}

export type HeroPatternType =
  | 'none'
  | 'dots'
  | 'grid'
  | 'circuit'
  | 'diagonal'
  | 'hexagons'
  | 'crosshatch';

export interface PageContentSiemensPartner {
  // Hero Section
  eyebrow: string;
  title: string;
  titleAccent: string;
  heroTitle?: string;
  lead: string;
  heroSubtitle?: string;
  primaryBtnText: string;
  verifyUrl: string;
  secondaryBtnText: string;
  secondaryBtnTarget: string;
  trustPoints: string[];
  partnerBadgeImage: string;
  badgeCaptionLeft: string;
  badgeStatusRight: string;
  heroBgImage?: string;
  heroBannerImage?: string;
  heroPattern?: HeroPatternType;
  heroBgType?: 'solid' | 'gradient';
  heroBgColor?: string;
  heroGradientFrom?: string;
  heroGradientTo?: string;
  heroOverlayOpacity?: number;

  // Proofbar Metrics (HUD)
  proofItems: SiemensProofMetric[];

  // Capabilities / What We Deliver
  capabilitiesEyebrow: string;
  capabilitiesHeading: string;
  capabilitiesDesc: string;
  capabilities: SiemensCapabilityCard[];
  capabilitiesConnectHeading?: string;
  capabilitiesConnectDesc?: string;
  capabilitiesConnectButtonText?: string;

  // Application Expertise
  expertiseEyebrow: string;
  expertiseHeading: string;
  expertiseDesc: string;
  techPills: string[];
  workflowSteps: SiemensWorkflowStep[];

  // Industry Experience (Repeatable Section with sync)
  industriesEyebrow: string;
  industriesHeading: string;
  industriesDesc: string;
  syncWithMasterIndustries?: boolean;
  industries: SiemensIndustryItem[];

  // Independent Verification (Certification)
  certEyebrow: string;
  certHeading: string;
  certDesc: string;
  certBtnText: string;
  certSteps: SiemensCertStep[];

  // Bottom CTA Section
  ctaHeading: string;
  ctaDesc: string;
  ctaBtnText: string;
  ctaBtnTarget?: string;

  // Legal / Branding Note
  legalNote: string;
}

export const DEFAULT_SIEMENS_PARTNER_CONTENT: PageContentSiemensPartner = {
  // Hero Section
  eyebrow: 'Siemens Solution Partner',
  title: 'Certified Siemens expertise.',
  titleAccent: 'Engineered by Simatech.',
  lead: 'Complete industrial automation and drive solutions — from system design and control-panel manufacturing to PLC/SCADA engineering, modernization, commissioning and lifecycle support.',
  primaryBtnText: 'Verify Partner Status on Siemens ↗',
  verifyUrl:
    'https://www.partnerfinder.automation.siemens.com/s/partner-details-page?language=en_US&acctId=001Mz00000ooD5LIAU&c__partnerType=Solution%2520Partner',
  secondaryBtnText: 'Explore Our Capabilities',
  secondaryBtnTarget: '#capabilities',
  trustPoints: [
    'Certified partner status',
    'Siemens automation & drives expertise',
    'Turnkey engineering capability',
  ],
  partnerBadgeImage: '/siemens_system_house.png',
  badgeCaptionLeft: 'Official Siemens Partner Finder identity',
  badgeStatusRight: 'Partner profile available',
  heroBgImage: '/images/about/hero_banner.jpg',
  heroBannerImage: '/images/about/hero_banner.jpg',
  heroPattern: 'dots',
  heroBgType: 'gradient',
  heroBgColor: '#06242a',
  heroGradientFrom: '#031417',
  heroGradientTo: '#06242a',
  heroOverlayOpacity: 85,

  // Proofbar
  proofItems: [
    {
      title: 'Since 2006',
      description: 'Industrial automation engineering experience',
      value: 'Since 2006',
      label: 'Industrial automation experience',
      isCyan: true,
      iconName: 'Award',
    },
    {
      title: 'Siemens Solution Partner',
      description: 'Official Partner Finder profile available',
      value: 'Siemens Partner',
      label: 'Authorized System House status',
      isCyan: false,
      iconName: 'ShieldCheck',
    },
    {
      title: 'Turnkey Capability',
      description: 'Design • Panels • Software • Commissioning',
      value: 'Turnkey Capability',
      label: 'Design • Panels • Software • Commissioning',
      isCyan: true,
      iconName: 'Cpu',
    },
    {
      title: 'Lifecycle Support',
      description: 'Modernization • Breakdown • AMC • Upgrades',
      value: 'Lifecycle Support',
      label: 'Modernization • Breakdown • AMC • Upgrades',
      isCyan: false,
      iconName: 'RefreshCw',
    },
  ],

  // Capabilities
  capabilitiesEyebrow: 'What we deliver',
  capabilitiesHeading: 'Siemens technology, integrated around your machine and process.',
  capabilitiesDesc:
    'Our focus is not component supply alone. Simatech engineers complete control systems around the application — selecting, integrating, programming, commissioning and supporting the automation architecture.',
  capabilities: [
    {
      id: 'plc',
      tag: 'PLC',
      title: 'SIMATIC Automation',
      description:
        'S7-1200, S7-1500 and distributed I/O solutions for machine and process automation, engineered with maintainability and diagnostics in mind.',
      iconName: 'Cpu',
      actionText: 'Inquire Solution Scope',
      actionTarget: 'modal:inquiry',
    },
    {
      id: 'hmi',
      tag: 'HMI',
      title: 'HMI & WinCC SCADA',
      description:
        'Operator interfaces, visualization, alarms, trends, data handling and supervisory systems designed around plant operating requirements.',
      iconName: 'Monitor',
      actionText: 'Inquire Solution Scope',
      actionTarget: 'modal:inquiry',
    },
    {
      id: 'drv',
      tag: 'DRV',
      title: 'SINAMICS Drives',
      description:
        'AC/DC drive engineering covering variable-speed, regenerative and high-performance multi-motor applications.',
      iconName: 'Zap',
      actionText: 'Inquire Solution Scope',
      actionTarget: 'modal:inquiry',
    },
    {
      id: 'mot',
      tag: 'MOT',
      title: 'Servo & Motion',
      description:
        'Servo-drive and motion solutions for coordinated axes, positioning, synchronization and precise machine control.',
      iconName: 'Activity',
      actionText: 'Inquire Solution Scope',
      actionTarget: 'modal:inquiry',
    },
    {
      id: 'upg',
      tag: 'UPG',
      title: 'Modernization & Migration',
      description:
        'Upgrade legacy control and drive systems with carefully engineered migration plans that minimize downtime and preserve machine functionality.',
      iconName: 'RefreshCw',
      actionText: 'Inquire Solution Scope',
      actionTarget: 'modal:inquiry',
    },
    {
      id: 'epc',
      tag: 'EPC',
      title: 'Turnkey Engineering',
      description:
        'Electrical design, control-panel manufacturing, software development, FAT, site commissioning, training and after-sales technical support.',
      iconName: 'Layers',
      actionText: 'Inquire Solution Scope',
      actionTarget: 'modal:inquiry',
    },
  ],
  capabilitiesConnectHeading: 'Need a tailored engineering architecture or drive scope?',
  capabilitiesConnectDesc:
    'Connect with our certified Siemens automation engineering team to discuss PLC, drive panels, SCADA, or turnkey integration.',
  capabilitiesConnectButtonText: 'Connect with Engineering Team',

  // Expertise
  expertiseEyebrow: 'Application expertise',
  expertiseHeading: 'Especially strong in high-performance machine automation.',
  expertiseDesc:
    'Simatech combines Siemens automation and drive technology with practical machine knowledge developed through commissioning, retrofit and troubleshooting assignments.',
  techPills: [
    'SIMATIC S7-1200 / S7-1500',
    'SINAMICS G120',
    'SINAMICS S120',
    'SINAMICS G130',
    'SINAMICS DCM',
    'V90 / S200 / S210',
    'WinCC SCADA',
    'PROFINET / PROFIBUS',
  ],
  workflowSteps: [
    {
      num: '01',
      phaseLabel: 'Phase 01',
      title: 'Engineering study',
      description:
        'Understand the process, machine sequence, speed/torque requirements, safety interfaces and existing architecture.',
      iconName: 'Compass',
    },
    {
      num: '02',
      phaseLabel: 'Phase 02',
      title: 'System architecture',
      description:
        'Select the appropriate Siemens PLC, HMI, drive, motion, networking and supervisory platform.',
      iconName: 'Sliders',
    },
    {
      num: '03',
      phaseLabel: 'Phase 03',
      title: 'Build & integrate',
      description:
        'Electrical engineering, control-panel manufacturing, software development and in-house functional testing.',
      iconName: 'Settings',
    },
    {
      num: '04',
      phaseLabel: 'Phase 04',
      title: 'Commission & support',
      description:
        'Site integration, commissioning, operator handover, troubleshooting, modernization and lifecycle support.',
      iconName: 'CheckCircle2',
    },
  ],

  // Industries (Syncable with Master Industries)
  industriesEyebrow: 'Industry experience',
  industriesHeading: 'Automation built for real industrial operating conditions.',
  industriesDesc:
    'Our engineering experience spans machine builders and end users across demanding manufacturing sectors, with particularly deep experience in Wire & Cable machinery.',
  syncWithMasterIndustries: true,
  industries: [
    {
      id: 'cable',
      name: 'Wire, Cable & Fiber Optic',
      detail:
        'Drawing, annealing, extrusion, stranding, armouring, drum twisters and related high-speed lines.',
      tag: 'Flagship Core',
      iconName: 'cable',
      link: '/industry',
    },
    {
      id: 'steel',
      name: 'Iron & Steel Plants',
      detail: 'Drive-intensive, rolling mills and coordinated machine/process applications.',
      tag: 'Heavy Mill Drives',
      iconName: 'steel',
      link: '/industry',
    },
    {
      id: 'pharma',
      name: 'Pharmaceuticals & Cleanrooms',
      detail: 'PLC/HMI/SCADA control and data-oriented 21 CFR Part 11 automation systems.',
      tag: '21 CFR Part 11',
      iconName: 'pharma',
      link: '/industry',
    },
    {
      id: 'chemical',
      name: 'Chemical & Continuous Processing',
      detail: 'Process automation, drive control, hazardous zone panels and plant integration.',
      tag: 'Ex-Proof IP55',
      iconName: 'chemical',
      link: '/industry',
    },
    {
      id: 'rubber',
      name: 'Rubber & Polymer Compounding',
      detail: 'Machine sequencing, Banbury mixers, calenders and temperature/process integration.',
      tag: 'Tension Control',
      iconName: 'rubber',
      link: '/industry',
    },
    {
      id: 'water',
      name: 'Water & Wastewater Treatment',
      detail: 'PLC, HMI/SCADA, networking and pumping process control integration.',
      tag: 'SCADA Telemetry',
      iconName: 'water',
      link: '/industry',
    },
    {
      id: 'furnace',
      name: 'Industrial Furnaces & Heat Treat',
      detail: 'Burner management, multi-zone pyrometry and high-temperature furnace controls.',
      tag: 'Multi-Zone PID',
      iconName: 'furnace',
      link: '/industry',
    },
    {
      id: 'factory',
      name: 'Specialized Machine OEMs',
      detail: 'Custom automation systems tailored for application-specific equipment and export machinery.',
      tag: 'OEM Precision',
      iconName: 'factory',
      link: '/industry',
    },
  ],

  // Certification / Independent Verification
  certEyebrow: 'Independent verification',
  certHeading: "Don’t just take our word for it. Verify us directly with Siemens.",
  certDesc:
    'Our Siemens Solution Partner profile and current partner expertise are available through the official Siemens Partner Finder. This gives customers an independent way to verify our partner status and the competencies recognized by Siemens.',
  certBtnText: 'Open Simatech in Siemens Partner Finder ↗',
  certSteps: [
    {
      title: 'Official partner profile',
      description: 'Use Siemens Partner Finder as the primary verification link.',
    },
    {
      title: 'Partner expertise & certificates',
      description:
        'Link visitors to Siemens for the current, authoritative certification information.',
    },
    {
      title: 'Optional local certificate gallery',
      description:
        'Your developer may also add Siemens-issued certificate PDFs supplied by Simatech, without altering their artwork.',
    },
  ],

  // CTA
  ctaHeading: 'Planning a Siemens automation or modernization project?',
  ctaDesc:
    'Talk to Simatech Automation about system architecture, drives, PLC/SCADA, retrofit or complete turnkey engineering.',
  ctaBtnText: 'Discuss Your Project',
  ctaBtnTarget: 'modal:inquiry',

  // Legal
  legalNote:
    'Branding note: Siemens, SIMATIC, SINAMICS and associated marks are trademarks of Siemens AG and/or its affiliates. The Siemens Solution Partner badge should be used only in accordance with the current branding material and usage permissions supplied by Siemens. Simatech Automation is an independent company.',
};
